<template>
    <div class="flex flex-col gap-y-2">
        <button
            ref="dropZoneRef"
            class="cursor-pointer rounded-xl border-2 border-dashed border-gray-400 bg-gray-50 p-6 text-center transition hover:bg-gray-100"
            type="button"
            @click="() => openFileDialog()"
        >
            <p class="text-gray-600">Drag & drop files here, or click to upload</p>
        </button>

        <div
            class="text-red-500"
            v-text="errorMessage"
        />
        <ul
            v-if="files.length"
            class="mt-4 space-y-2"
        >
            <li
                v-for="(file, index) in files"
                :key="file.name"
                class="flex items-center justify-between space-x-2"
            >
                <div class="flex items-center space-x-2">
                    <span class="font-medium text-gray-800">{{ file.name }}</span>
                    <span class="text-sm text-gray-500">({{ formatSize(file.size) }})</span>
                </div>
                <button
                    class="text-red-500 hover:text-red-700"
                    type="button"
                    @click="removeFile(index)"
                >
                    Remove
                </button>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { idGen } from '@/utilities/idGen';
import { useDropZone, useFileDialog } from '@vueuse/core';

const props = withDefaults(
    defineProps<{
        vid: string;
        multiple: boolean;
    }>(),
    {
        vid: idGen(6),
        multiple: true,
    },
);

const { value: files, errorMessage } = useField<File[]>(toRef(props, 'vid'), undefined, {
    initialValue: [],
});

const removeFile = (index: number) => {
    files.value = files.value.filter((_, i) => i !== index);
};

const onDrop = (droppedFiles: File[] | FileList | null) => {
    if (!droppedFiles) return;
    files.value = [...files.value, ...Array.from(droppedFiles)];
};

const dropZoneRef = useTemplateRef('dropZoneRef');
useDropZone(dropZoneRef, {
    onDrop,
});

const { open: openFileDialog, onChange } = useFileDialog({
    multiple: props.multiple,
    accept: '*/*',
});
onChange(onDrop);

const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<style scoped>
/* Optional: smoother drag effect */
[data-v-dropzone] {
    transition: background-color 0.2s ease;
}
</style>
